import { useMemo } from "react";

import { useRoleContext } from "./useRoleContext";
import { useSwrFetch } from "./useSwrFetch";

interface UseShipperQuoteCompetitivenessScoresProps {
  shipmentId: string | number | undefined;
}

interface UseShipperQuoteCompetitivenessScoresResult {
  scoreByQuoteId: Map<number, ShipperQuoteScore>;
  loading: boolean;
  error: string | null;
}

/**
 * Every scored quote's competitiveness on a shipment, keyed by quote id
 * (docs/engineering/design/quote_competitiveness_scoring_design.md §14).
 */
export function useShipperQuoteCompetitivenessScores(
  props: UseShipperQuoteCompetitivenessScoresProps,
): UseShipperQuoteCompetitivenessScoresResult {
  const { shipmentId } = props;
  const { role } = useRoleContext();

  const url = useMemo(() => {
    if (role?.type !== "SHIPPER" || !shipmentId) {
      return null;
    }
    return `/shipments/${shipmentId}/quotes/competitiveness-scores`;
  }, [role, shipmentId]);

  const { data, loading, error } =
    useSwrFetch<ShipperQuoteCompetitivenessScoresResponse>({
      url,
      errorMessage: "Failed to fetch quote competitiveness scores",
    });

  const scoreByQuoteId = useMemo(
    () =>
      new Map(
        (data?.quote_scores ?? []).map((quoteScore) => [
          quoteScore.quote_id,
          quoteScore,
        ]),
      ),
    [data],
  );

  return { scoreByQuoteId, loading, error };
}
